import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  Currency as CurrencyType,
  FiatCurrency,
} from '__generated__/globalTypes';
import { Select } from 'atoms/inputs/Select';
import { Vertical } from 'atoms/layout/flex';
import { BodyM, BodyS } from 'atoms/typography';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useUpdateCurrency } from 'hooks/useUpdateCurrency';
import { useFiatBalance } from 'hooks/wallets/useFiatBalance';
import { userAttributes } from 'lib/glossary';

import { SettingsSection } from '../SettingsSection';

const Group = styled(Vertical)`
  align-items: flex-start;
`;

const Label = styled(Vertical)`
  padding-right: var(--double-unit);
`;

const messages = defineMessages({
  title: {
    id: 'Settings.currency.title',
    defaultMessage: 'Market currency',
  },
  currencyDetails: {
    id: 'Settings.currency.currencyDetails',
    defaultMessage:
      'Changing your primary currency will update how card prices are displayed. It will not impact the currency preferences in your Cash Wallet or ETH Wallet.',
  },
  primaryCurrency: {
    id: 'Settings.currency.primaryCurrency',
    defaultMessage: 'Primary currency',
  },
  fiatCurrencyDetails: {
    id: 'Settings.currency.fiatCurrencyDetails',
    defaultMessage:
      'Choose the currency that you would like to see displayed across Sorare, including your wallet and the marketplace.',
  },
});

export const Currency = () => {
  const { updateCurrency, loading } = useUpdateCurrency();
  const {
    currentUser,
    walletPreferences: { showEthWallet, showFiatWallet },
  } = useCurrentUserContext();
  const { canListAndTrade } = useFiatBalance();
  if (!currentUser) return null;
  const { userSettings } = currentUser;

  const { currency, fiatCurrency } = userSettings;

  const currencies = Object.values(CurrencyType).map(v => ({
    label:
      showEthWallet && showFiatWallet && v === CurrencyType.FIAT
        ? fiatCurrency?.toString()
        : v.toString(),
    value: v,
  }));

  const selectedCurrency = currencies.find(c => c.value === currency);
  const fiatCurrencies = Object.values(FiatCurrency).map(v => ({
    label: v.toString(),
    value: v,
  }));

  const selectedFiatCurrency = fiatCurrencies.find(
    c => c.value === fiatCurrency
  );

  if (!showEthWallet && canListAndTrade) return null;

  return (
    <SettingsSection title={messages.title}>
      {showEthWallet && (
        <Group>
          <Label>
            <BodyM color="var(--c-white)">
              <FormattedMessage
                {...(canListAndTrade
                  ? messages.primaryCurrency
                  : userAttributes.currency)}
              />
            </BodyM>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage {...messages.currencyDetails} />
            </BodyS>
          </Label>
          <Select
            small
            menuLateralAlignment="left"
            icon={faChevronDown}
            value={selectedCurrency}
            onChange={updateCurrency('currency')}
            options={currencies}
            isDisabled={loading}
          />
        </Group>
      )}
      {!canListAndTrade && (
        <Group>
          <Label>
            <BodyM color="var(--c-white)">
              <FormattedMessage {...userAttributes.fiatCurrency} />
            </BodyM>
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage {...messages.fiatCurrencyDetails} />
            </BodyM>
          </Label>
          <Select
            small
            menuLateralAlignment="left"
            icon={faChevronDown}
            value={selectedFiatCurrency}
            onChange={updateCurrency('fiatCurrency')}
            options={fiatCurrencies}
            isDisabled={loading}
          />
        </Group>
      )}
    </SettingsSection>
  );
};
